"use client";

// ---------------------------------------------------------------------------
// FRAMEWORK-SPECIFIC LINE — this is the ONLY one in the file.
// Next.js App Router. Swap when porting:
//   Next.js Pages Router → import { useRouter } from "next/router"  (router.asPath)
//   React Router         → import { useLocation } from "react-router-dom"
//   TanStack Router      → useRouterState({ select: (s) => s.location.pathname })
//   Plain SPA / no router→ const pathname = window.location.pathname (won't
//                          re-evaluate on client-side navigation)
// The hook exists so the exempt-route check re-runs on client-side navigation;
// a one-shot window.location read would leave /admin protected after a soft nav.
import { usePathname } from "next/navigation";
// ---------------------------------------------------------------------------

import { useEffect } from "react";

/**
 * ContentProtection — client-side copy deterrent.
 *
 * WHAT THIS IS: friction for casual copy/paste and right-click-save. Nothing
 * more. The markup is still in view-source, in `curl`, in the Next.js RSC
 * payload, and in every crawler's index. Disabling JavaScript removes all of
 * it. Treat this as a speed bump, not a security control — never put anything
 * here that actually needs to stay secret.
 *
 * DEVTOOLS ARE DELIBERATELY NOT BLOCKED. There used to be a `blockDevtools`
 * switch here that swallowed the context menu, F12, Ctrl+Shift+I/J/C and
 * Ctrl+U. It was removed on purpose: DevTools (the Network panel in
 * particular) is needed to debug this site, the block stopped nobody who
 * wanted DevTools anyway — the browser's own menu and keyboard preferences
 * both bypass a keydown handler — and its real cost was visitors losing
 * right-click → "Open link in new tab". Do not add it back.
 *
 * WHY THE CARVE-OUTS EXIST — READ BEFORE DELETING ANY OF THEM:
 *
 *  1. Form fields (input / textarea / select / [contenteditable="true"]).
 *     `user-select: none` inherits, and a blanket `copy`/`keydown` block kills
 *     Ctrl+A, Ctrl+C, Ctrl+X and Ctrl+V *inside the fields too*. Remove these
 *     and the contact form stops accepting pasted emails and phone numbers —
 *     which is the single most common way visitors fill it in. Every handler
 *     therefore tests `closest()` against those selectors, not just
 *     `event.target`, because the real target is usually a child element
 *     (a <span> inside a contenteditable, say) rather than the field itself.
 *
 *  2. Exempt routes (`exemptPaths`). The CMS and post editors need ordinary
 *     clipboard behaviour to be usable at all. On those paths the effect bails
 *     out before injecting anything.
 *
 *  3. `.allow-select`. Phone numbers, addresses, prices and email addresses
 *     that visitors are *supposed* to copy. Blocking those costs conversions;
 *     it doesn't protect anything.
 *
 * All state is undone on cleanup: listeners removed, injected <style> removed,
 * body class removed. Mount once, in the root layout.
 */

const STYLE_ELEMENT_ID = "content-protection-style";
const PROTECTED_BODY_CLASS = "content-protection-active";
const ALLOW_SELECT_CLASS = "allow-select";

/**
 * Anything matching this stays fully selectable and fully clipboard-enabled.
 * Kept as one string so the CSS and the JS handlers can never drift apart.
 */
const EDITABLE_SELECTOR = [
  "input",
  "textarea",
  "select",
  "option",
  '[contenteditable="true"]',
  '[contenteditable=""]',
  `.${ALLOW_SELECT_CLASS}`,
].join(", ");

/**
 * Module-level constant so the default keeps a stable identity across renders
 * and the effect below doesn't re-run on every parent render. If you pass your
 * own array, hoist it out of the render body (or useMemo it) for the same
 * reason.
 */
const DEFAULT_EXEMPT_PATHS = ["/admin", "/dashboard"];

export interface ContentProtectionProps {
  /**
   * Route prefixes left completely unprotected — no style, no listeners.
   * A prefix matches the path itself and everything beneath it:
   * "/admin" covers "/admin" and "/admin/posts/3", but not "/administration".
   */
  exemptPaths?: string[];
  /**
   * Text selection, copy/cut, image dragging, and Ctrl/Cmd+C / X / A / S.
   * Active in development too, so local behaviour matches production.
   */
  blockCopy?: boolean;
  /**
   * When false (the default) Ctrl/Cmd+P is swallowed and a print stylesheet
   * blanks the page, since the browser's own File → Print menu bypasses the
   * key handler entirely. Set true if the site has printable content
   * (invoices, checklists) — key blocking alone would be theatre.
   */
  allowPrint?: boolean;
}

/** Normalises an event target (which may be a text node) to an Element. */
function toElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

/** True when the node sits inside a field/opt-out where we must not interfere. */
function isExemptNode(node: EventTarget | Node | null): boolean {
  const element = toElement(node as EventTarget | null);
  return element ? element.closest(EDITABLE_SELECTOR) !== null : false;
}

/**
 * Copy/cut fire at the element holding the *selection*, which is not always
 * the element the user last clicked — so check both before blocking.
 */
function selectionIsExempt(): boolean {
  const selection = typeof window !== "undefined" ? window.getSelection() : null;
  if (!selection || selection.rangeCount === 0) return false;
  return (
    isExemptNode(selection.anchorNode) || isExemptNode(selection.focusNode)
  );
}

export function ContentProtection({
  exemptPaths = DEFAULT_EXEMPT_PATHS,
  blockCopy = true,
  allowPrint = false,
}: ContentProtectionProps) {
  const pathname = usePathname();

  useEffect(() => {
    const currentPath = pathname ?? "/";
    const isExemptRoute = exemptPaths.some(
      (prefix) => currentPath === prefix || currentPath.startsWith(`${prefix}/`)
    );
    // CMS / editor routes: attach nothing at all.
    if (isExemptRoute) return;

    const { body } = document;
    const cleanups: Array<() => void> = [];

    // --- injected stylesheet -------------------------------------------------
    // Self-contained on purpose: the component brings its own CSS so it can be
    // dropped into a project without touching that project's stylesheets.
    const rules: string[] = [];

    if (blockCopy) {
      rules.push(`
      body.${PROTECTED_BODY_CLASS} {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        /* Suppresses the iOS long-press "Copy / Look Up" callout. */
        -webkit-touch-callout: none;
      }
      /* Images: belt-and-braces with the dragstart handler below. */
      body.${PROTECTED_BODY_CLASS} img,
      body.${PROTECTED_BODY_CLASS} a {
        -webkit-user-drag: none;
        -khtml-user-drag: none;
      }
      /* CARVE-OUT — do not delete: without this the contact form's fields
         inherit user-select:none and become unusable on touch devices. */
      body.${PROTECTED_BODY_CLASS} :is(${EDITABLE_SELECTOR}),
      body.${PROTECTED_BODY_CLASS} :is(${EDITABLE_SELECTOR}) * {
        -webkit-user-select: text;
        -moz-user-select: text;
        -ms-user-select: text;
        user-select: text;
        -webkit-touch-callout: default;
      }`);
    }

    if (!allowPrint) {
      rules.push(`
      @media print {
        body.${PROTECTED_BODY_CLASS} { display: none !important; }
      }`);
    }

    if (rules.length > 0) {
      const style = document.createElement("style");
      style.id = STYLE_ELEMENT_ID;
      style.textContent = rules.join("\n");
      document.head.appendChild(style);
      body.classList.add(PROTECTED_BODY_CLASS);
      cleanups.push(() => {
        body.classList.remove(PROTECTED_BODY_CLASS);
        style.remove();
      });
    }

    // --- handlers ------------------------------------------------------------
    const listen = <K extends keyof DocumentEventMap>(
      type: K,
      handler: (event: DocumentEventMap[K]) => void
    ) => {
      document.addEventListener(type, handler);
      cleanups.push(() => document.removeEventListener(type, handler));
    };

    const blockUnlessExempt = (event: Event) => {
      if (isExemptNode(event.target)) return;
      event.preventDefault();
    };

    const onClipboard = (event: Event) => {
      // Two checks: where the pointer is, and where the selection is.
      if (isExemptNode(event.target) || selectionIsExempt()) return;
      event.preventDefault();
    };

    if (blockCopy) {
      listen("dragstart", blockUnlessExempt);
      listen("copy", onClipboard);
      listen("cut", onClipboard);
    }

    // NOTE: no "contextmenu" listener. Right-click → Inspect is a DevTools
    // entry point and must keep working — see the note at the top of the file.

    const onKeyDown = (event: KeyboardEvent) => {
      // Inside a field, every shortcut stays native — Ctrl+A/C/X/V included.
      if (isExemptNode(event.target) || isExemptNode(document.activeElement)) {
        return;
      }

      const key = (event.key || "").toLowerCase();
      const modifier = event.ctrlKey || event.metaKey;

      // F12, Ctrl+Shift+I/J/C and Ctrl+U are intentionally left alone.
      if (!modifier) return;

      // c/x = copy/cut, a = select all, s = save page.
      if (blockCopy && ["c", "x", "a", "s"].includes(key)) {
        event.preventDefault();
        return;
      }
      if (key === "p" && !allowPrint) {
        event.preventDefault();
      }
    };

    listen("keydown", onKeyDown);

    // --- cleanup: leave no trace --------------------------------------------
    return () => {
      cleanups.forEach((undo) => undo());
    };
  }, [pathname, exemptPaths, blockCopy, allowPrint]);

  return null;
}

export default ContentProtection;
